import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { api } from './api';

export const STATUS_COLORS = {
  scheduled: '#00AAFF',
  confirmed: '#22c55e',
  completed: '#94a3b8',
  cancelled: '#ef4444',
  no_show: '#f97316',
};

export const APPOINTMENT_TYPE_LABELS = {
  intro_call: 'Intro Call',
  presentation: 'Presentation',
  follow_up: 'Follow-Up',
  closing_call: 'Closing Call',
};

export const APPOINTMENT_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' },
];

export const APPOINTMENT_TYPES_FILTER = [
  { value: '', label: 'All Types' },
  { value: 'intro_call', label: 'Intro Call' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'closing_call', label: 'Closing Call' },
];

export const GRID_START_HOUR = 7;
export const GRID_END_HOUR = 21;
export const GRID_TOTAL_MINUTES = (GRID_END_HOUR - GRID_START_HOUR) * 60;

export const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function statusColor(status) {
  return STATUS_COLORS[String(status || '').toLowerCase()] || STATUS_COLORS.scheduled;
}

export function leadDisplayName(appt) {
  const l = appt?.lead;
  if (l?.first_name || l?.last_name) {
    return [l.first_name, l.last_name].filter(Boolean).join(' ');
  }
  return appt?.title || 'Appointment';
}

export function getInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase() || '?';
}

export function getMonthRange(date) {
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  return { startDate: start.toISOString(), endDate: endOfDay(end).toISOString() };
}

export function getWeekRange(date) {
  const start = startOfWeek(date, { weekStartsOn: 0 });
  const end = endOfWeek(date, { weekStartsOn: 0 });
  return { startDate: start.toISOString(), endDate: endOfDay(end).toISOString() };
}

export function getDayRange(date) {
  const start = startOfDay(date);
  const end = endOfDay(date);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

export function getViewRange(viewMode, focusDate) {
  if (viewMode === 'week') return getWeekRange(focusDate);
  if (viewMode === 'day') return getDayRange(focusDate);
  return getMonthRange(focusDate);
}

export function filterAppointments(appointments, { agentId, type, status }) {
  return (appointments || []).filter((a) => {
    if (agentId && a.assigned_to !== agentId) return false;
    if (type && a.appointment_type !== type) return false;
    if (status && a.status !== status) return false;
    return true;
  });
}

export function appointmentsForDay(appointments, day) {
  return (appointments || [])
    .filter((a) => a.scheduled_at && isSameDay(parseISO(a.scheduled_at), day))
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
}

export function buildMonthGrid(focusDate) {
  const monthStart = startOfMonth(focusDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const cells = [];
  let cursor = gridStart;
  for (let i = 0; i < 42; i += 1) {
    cells.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return cells;
}

export function formatTimeRange(appt) {
  if (!appt?.scheduled_at) return '—';
  const start = parseISO(appt.scheduled_at);
  const dur = Number(appt.duration_minutes) || 30;
  const end = new Date(start.getTime() + dur * 60 * 1000);
  return `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`;
}

export function formatShortTime(iso) {
  if (!iso) return '';
  return format(parseISO(iso), 'h:mm a');
}

export function appointmentEnd(appt) {
  const start = parseISO(appt.scheduled_at);
  const dur = Number(appt.duration_minutes) || 30;
  return new Date(start.getTime() + dur * 60 * 1000);
}

export function minutesFromGridStart(date) {
  return date.getHours() * 60 + date.getMinutes() - GRID_START_HOUR * 60;
}

export function blockStyle(appt) {
  const start = parseISO(appt.scheduled_at);
  const end = appointmentEnd(appt);
  const topMin = Math.max(0, minutesFromGridStart(start));
  const endMin = Math.min(GRID_TOTAL_MINUTES, minutesFromGridStart(end));
  const heightMin = Math.max(18, endMin - topMin);
  return {
    top: `${(topMin / GRID_TOTAL_MINUTES) * 100}%`,
    height: `${(heightMin / GRID_TOTAL_MINUTES) * 100}%`,
  };
}

export function slotIsoForDayAndMinutes(day, totalMinutes) {
  const d = startOfDay(day);
  d.setHours(GRID_START_HOUR + Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  return d.toISOString();
}

export function isOutsideMonth(day, focusDate) {
  return !isSameMonth(day, focusDate);
}

export function isToday(day) {
  return isSameDay(day, new Date());
}

export function mergeAppointment(existing, incoming) {
  if (!incoming?.id) return existing;
  const idx = (existing || []).findIndex((a) => a.id === incoming.id);
  if (idx === -1) return [...(existing || []), incoming];
  const next = [...existing];
  next[idx] = { ...next[idx], ...incoming };
  return next;
}

export async function deleteGoogleEventsForAppointment(appt) {
  const assigneeId = appt.assigned_to;
  if (!assigneeId) return;
  const tasks = [];
  if (appt.google_event_id_agent) {
    tasks.push(
      api.googleCalendar.deleteEvent(assigneeId, 'agent', appt.google_event_id_agent).catch(() => {}),
    );
  }
  if (appt.google_event_id_agency) {
    tasks.push(
      api.googleCalendar.deleteEvent(assigneeId, 'agency', appt.google_event_id_agency).catch(() => {}),
    );
  }
  await Promise.all(tasks);
}
